import { useEffect } from "react";
import { AppState, Dimensions, Keyboard, type View } from "react-native";
import {
  KeyboardController,
  useGenericKeyboardHandler,
  useKeyboardContext,
} from "react-native-keyboard-controller";
import { scheduleOnRN } from "react-native-worklets";

import { probe } from "@/lib/timing-probe";

/**
 * MEASUREMENT ONLY. Nothing here changes how the keyboard behaves.
 *
 * Two questions: (1) does the composer clear the keyboard (build-13 report;
 * discriminator composerBottom vs kbTop), and (2) does the app return from a
 * lock with the keyboard's space still reserved (builds 14/16/17)? In the
 * faulty state NO keyboardDidShow/DidHide fires, so only an AppState-driven
 * read and the worklet-level move events can see it.
 *
 * Build 17 caught the fault three times: `libVisible:0 libHeight:0` with the
 * composer at rest at resume (752/827), then lifted 300 px (452/527) over
 * ~600 ms. That killed the standing theory twice — the library does NOT
 * believe a keyboard is open, and the lift happens AFTER resume, so a
 * dismiss on the way out could never touch it. It also proved build 17
 * measured the wrong channel: `KeyboardController.state()` is JS module
 * state, while KeyboardAvoidingView interpolates its own LOCAL shared
 * values, seeded once at mount and driven only by workletized
 * onStart/onMove/onEnd. Three sources; at least two disagree on device.
 *
 * So this version reads the two that drive pixels — `uiProgress`/`uiHeight`
 * (KeyboardContext.reanimated, the global truth) and `keyboard.move.*` (the
 * worklet events, at onStart/onEnd). The pair is the discriminator, and the
 * answers need different fixes:
 *   uiProgress > 0 while libVisible 0  -> global false belief; reconcile at
 *   resume.   uiProgress == 0, composer lifted -> the view's local copy
 *   diverged; fix at the view.
 *
 * useKeyboardContext(), NOT useReanimatedKeyboardAnimation(): the latter
 * mounts useResizeMode, putting the probe in charge of Android's soft-input
 * mode (measurement changing the measured; unmount would reset a mode a live
 * component depends on). useGenericKeyboardHandler for the same reason —
 * additive registration, cannot displace KeyboardAvoidingView's handlers.
 */

/**
 * How long after `keyboardDidShow` to read the geometry.
 *
 * The build-14 and build-16 numbers were taken AT `keyboardDidShow` and three
 * of them showed the composer apparently under the keyboard — disproved against
 * video frames 200ms later. `didShow` fires when the KEYBOARD has finished
 * arriving, which is not when `KeyboardAvoidingView`'s padding has finished
 * following it, so every one of those samples was read mid-animation. A probe
 * that reliably reports a fault that is not there is worse than no probe.
 *
 * 400ms clears the ~250ms platform keyboard animation with room to spare.
 */
const SETTLE_MS = 400;

/**
 * When to sample after the app becomes active, in ms.
 *
 * Build 17 sampled at 0 and 600 and found the composer at rest at the first and
 * lifted at the second — which brackets the event but does not locate it, and
 * "somewhere in a 600ms window" is not enough to say whether the lift rides an
 * insets animation or lands in one step. Five reads bracket it to ~150ms and the
 * 1200 tail answers the other open question: whether the state ever settles back
 * on its own, or stays wrong until the input is tapped.
 *
 * Bounded by construction — a resume is a user action, and the whole ladder is
 * five log lines.
 */
const RESUME_SAMPLES_MS = [0, 150, 300, 600, 1200];

/** `onStart` / `onEnd`, as a number because probes carry only numbers. */
const MOVE_START = 0;
const MOVE_END = 1;

/**
 * Reported from the UI thread via `scheduleOnRN`.
 *
 * Module scope so the worklet captures a plain function reference. ONLY the
 * start and end of a movement — `onMove` fires every frame and probing it would
 * turn a diagnostic into a flood, and worse, would put a JS hop on the UI
 * thread's animation path.
 */
function reportMove(
  screen: string,
  phase: number,
  progress: number,
  height: number,
): void {
  probe(`keyboard.move.${screen}`, { phase, progress, height });
}

export type KeyboardProbeScreen = "chat" | "group" | "practice";

export function useKeyboardProbe(
  composerRef: React.RefObject<View | null>,
  /** Goes in the probe NAME, because `probe` carries only finite numbers. */
  screen: KeyboardProbeScreen,
): void {
  // Destructured rather than held whole: `KeyboardProvider` rebuilds the context
  // object on `enabled` changes, but `progress` and `height` are `useSharedValue`
  // handles created once for the app's lifetime. Naming the two values as effect
  // dependencies is therefore honest AND stable — holding the context object
  // instead would churn the listener on a change that did not touch them.
  const { progress: keyboardProgress, height: keyboardHeight } =
    useKeyboardContext().reanimated;

  useGenericKeyboardHandler(
    {
      onStart: (event) => {
        "worklet";
        scheduleOnRN(reportMove, screen, MOVE_START, event.progress, event.height);
      },
      onEnd: (event) => {
        "worklet";
        scheduleOnRN(reportMove, screen, MOVE_END, event.progress, event.height);
      },
    },
    [screen],
  );

  useEffect(() => {
    const timers = new Set<ReturnType<typeof setTimeout>>();

    /**
     * `kbHeight`/`kbTop` are what the KEYBOARD event said; `libVisible`/
     * `libHeight` are the JS module's belief; `uiProgress`/`uiHeight` are the
     * shared values that actually drive the padding. Build 17 proved the first
     * two can read "no keyboard" while the composer is lifted, so the third pair
     * is the one that carries the diagnosis.
     *
     * `windowHeight` catches the remaining possibility — a reported keyboard
     * height that excludes a vendor toolbar row (the device is a Samsung), which
     * shows up as `kbTop + kbHeight` failing to reach the bottom of the window.
     */
    const sample = (
      name: string,
      keyboard: { height: number; screenY: number },
      extra: Record<string, number> = {},
    ) => {
      const window = Dimensions.get("window");
      // Read once, before the async measure callback, so every field in a line
      // describes the same instant.
      const uiProgress = keyboardProgress.value;
      const uiHeight = keyboardHeight.value;
      const libVisible = KeyboardController.isVisible() ? 1 : 0;
      const libHeight = KeyboardController.state().height;

      const report = (composerTop: number, composerHeight: number) => {
        probe(name, {
          ...extra,
          kbHeight: Math.round(keyboard.height),
          kbTop: Math.round(keyboard.screenY),
          libVisible,
          libHeight: Math.round(libHeight),
          uiProgress,
          // The library carries this NEGATIVE (it is meant to be used directly
          // as a translateY), so it is reported as its magnitude to compare
          // against `libHeight` and against the composer's own displacement
          // without a sign flip in the reader's head.
          uiHeight: Math.round(Math.abs(uiHeight)),
          windowHeight: Math.round(window.height),
          composerTop: Math.round(composerTop),
          composerBottom: Math.round(composerTop + composerHeight),
        });
      };

      const node = composerRef.current;
      if (!node || typeof node.measureInWindow !== "function") {
        // Still worth the sample: the shared values alone separate stuck padding
        // from a keyboard that genuinely came back.
        report(-1, 0);
        return;
      }
      node.measureInWindow((_x, y, _width, height) => report(y, height));
    };

    const later = (ms: number, run: () => void) => {
      const timer = setTimeout(() => {
        timers.delete(timer);
        run();
      }, ms);
      timers.add(timer);
    };

    const shown = Keyboard.addListener("keyboardDidShow", (event) => {
      // Read once the avoidance has settled — see SETTLE_MS. The event's own
      // coordinates are captured now because the event object is not ours to
      // hold onto.
      const { height, screenY } = event.endCoordinates;
      later(SETTLE_MS, () => sample(`keyboard.show.${screen}`, { height, screenY }));
    });

    // A resume has no keyboard event to hang off — that is the point — so the
    // keyboard's own metrics come from the library's last known state rather
    // than from an event, and `late` says which rung of the ladder this is.
    const resumed = AppState.addEventListener("change", (next) => {
      if (next !== "active") return;
      RESUME_SAMPLES_MS.forEach((delay, index) => {
        const read = () =>
          sample(
            `keyboard.resume.${screen}`,
            { height: KeyboardController.state().height, screenY: -1 },
            { late: index, afterMs: delay },
          );
        if (delay === 0) read();
        else later(delay, read);
      });
    });

    return () => {
      shown.remove();
      resumed.remove();
      for (const timer of timers) clearTimeout(timer);
      timers.clear();
    };
    // Web has no software keyboard events worth sampling here, but the
    // listeners are harmless and the probe is a no-op off a debug build.
  }, [composerRef, keyboardHeight, keyboardProgress, screen]);
}
